import React, { useState, useEffect } from "react";

const PPFCalculator = () => {
  const [yearlyDeposit, setYearlyDeposit] = useState(150000);
  const [annualInterestRate, setAnnualInterestRate] = useState(7.1);
  const [tenure, setTenure] = useState(15);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState({
    futureValue: "0.00",
    totalInvestment: "0.00",
    estimatedReturns: "0.00",
    inflationAdjustedValue: "0.00",
  });

  // PPF Calculation Function
  const calculatePPF = () => {
    const P = parseFloat(yearlyDeposit);
    const r = parseFloat(annualInterestRate) / 100;
    const n = parseFloat(tenure);
    const inflation = parseFloat(inflationRate) / 100;

    if (isNaN(P) || isNaN(r) || isNaN(n) || P <= 0 || n <= 0) return;

    // PPF Formula: M = P × [((1 + r)^n - 1) / r]
    // Interest is compounded annually
    let maturityAmount = 0;
    for (let i = 1; i <= n; i++) {
      maturityAmount += P * Math.pow(1 + r, n - i + 1);
    }
    
    const totalDeposit = P * n;
    const interestEarned = maturityAmount - totalDeposit;
    
    // Calculate inflation-adjusted value
    const inflationAdjustedValue = maturityAmount / Math.pow(1 + inflation, n);

    setResult({
      futureValue: maturityAmount.toFixed(2),
      totalInvestment: totalDeposit.toFixed(2),
      estimatedReturns: interestEarned.toFixed(2),
      inflationAdjustedValue: inflationAdjustedValue.toFixed(2),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
  calculatePPF();
}, [yearlyDeposit, annualInterestRate, tenure, inflationRate]);


  const growthPercentage = result.totalInvestment > 0 
    ? ((Number(result.estimatedReturns) / Number(result.totalInvestment)) * 100).toFixed(1)
    : 0;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1>PPF Calculator</h1>
          <p>
            Calculate your Public Provident Fund maturity amount with guaranteed returns and tax benefits
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                PPF Details
              </h2>

              {/* Yearly Deposit */}
              <div className="input-group">
                <label>Yearly Deposit</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={yearlyDeposit}
                    onChange={(e) => setYearlyDeposit(e.target.value)}
                    className="calc-input"
                    placeholder="150000"
                  />
                </div>
                <div className="quick-select-group">
                  {[50000, 100000, 150000, 500000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setYearlyDeposit(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Annual Interest Rate */}
              <div className="input-group">
                <label>Annual Interest Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={annualInterestRate}
                    onChange={(e) => setAnnualInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="7.1"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[7, 7.1, 7.5, 8].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setAnnualInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Tenure */}
              <div className="input-group">
                <label>Tenure (years)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={tenure}
                    onChange={(e) => setTenure(e.target.value)}
                    className="calc-input"
                    placeholder="15"
                  />
                </div>
                <div className="quick-select-group">
                  {[15, 20, 25, 30].map((years) => (
                    <button
                      key={years}
                      onClick={() => setTenure(years)}
                      className="quick-select-btn success"
                    >
                      {years}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Expected Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculatePPF} className="calculate-btn">
                Calculate PPF Returns
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                PPF Summary
              </h2>

              <div className="result-card">
                {/* Total Deposit */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Investment</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInvestment).toLocaleString("en-IN")}
                  </div>
                </div>

                {/* Interest Earned */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Interest Earned</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.estimatedReturns).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext green">
                    +{growthPercentage}% Growth (Tax-Free)
                  </div>
                </div>

                {/* Maturity Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Maturity Amount</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.futureValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After {tenure} years at {annualInterestRate}% interest
                  </div>
                </div>

                {/* Inflation Adjusted Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Inflation Adjusted Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.inflationAdjustedValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Real value in today's terms
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">PPF Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Your Investment</span>
                      <span className="breakdown-percentage">
                        {((Number(result.totalInvestment) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(Number(result.totalInvestment) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest Earned</span>
                      <span className="breakdown-percentage green">
                        {((Number(result.estimatedReturns) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(Number(result.estimatedReturns) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the PPF Calculator Work?</h3>
              <p>
                The PPF calculator estimates your maturity amount by calculating compound interest on 
                your yearly deposits. PPF is a government-backed long-term savings scheme with EEE 
                (Exempt-Exempt-Exempt) tax benefits. Interest is compounded annually and added to your 
                account, helping your investment grow substantially over the minimum 15-year tenure.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              M = P × [((1 + r)^n - 1) / r] × (1 + r)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">M</div>
                <div className="formula-param-value">Maturity Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Yearly Deposit</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Annual Interest Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Tenure in Years</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is Public Provident Fund (PPF)?</h5>
              <p>
                PPF is a long-term government-backed savings scheme with a lock-in period of 15 years. 
                It offers guaranteed returns with complete tax exemption under Section 80C. You can 
                invest a minimum of ₹500 and maximum of ₹1.5 lakh per financial year. PPF is one of 
                the safest investment options in India with zero risk and sovereign guarantee.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the tax benefits of PPF?</h5>
              <p>
                PPF enjoys EEE (Exempt-Exempt-Exempt) status - deposits qualify for deduction under 
                Section 80C (up to ₹1.5 lakh), interest earned is completely tax-free, and maturity 
                amount is also tax-exempt. This makes PPF one of the most tax-efficient investment 
                options available. Unlike bank FDs, you don't pay any tax on PPF interest.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I withdraw money from PPF before maturity?</h5>
              <p>
                Partial withdrawals are allowed from the 7th year onwards, limited to 50% of the 
                balance at the end of 4th year or preceding year, whichever is lower. Complete 
                premature closure is permitted only in specific cases like serious illness or higher 
                education after 5 years. The account automatically extends in 5-year blocks after 
                the initial 15 years.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is PPF interest calculated?</h5>
              <p>
                PPF interest is calculated on the lowest balance between the 5th and last day of 
                every month and credited annually on March 31st. To maximize interest, deposit 
                before the 5th of every month. The current interest rate is 7.1% per annum 
                (compounded annually), which is revised quarterly by the government based on 
                prevailing rates.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I have multiple PPF accounts?</h5>
              <p>
                No, an individual can have only one PPF account in their name. However, you can 
                open a PPF account for your minor child, but the combined annual deposit limit of 
                ₹1.5 lakh applies to both accounts. If you have multiple accounts, only the first 
                opened account will earn interest; others will be closed without interest.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I take a loan against my PPF account?</h5>
              <p>
                Yes, you can take a loan from your PPF account from the 3rd to 6th financial year. 
                The maximum loan amount is 25% of the balance at the end of 2nd preceding year. 
                The loan must be repaid within 36 months with interest (currently 1% above PPF rate). 
                Only one loan can be active at a time, and you can't take a loan if you've made a 
                partial withdrawal.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PPFCalculator;